import { X, Copy, Wand2, Clock, Sparkles, Loader2, AlertCircle } from 'lucide-react';
import type { IterationGraphNode } from './api';
import { formatInBeijing } from '../../../utils/time';
import { resolveBrowserMediaUrl } from '../../../utils/storageUrl';

interface IterationNodeDetailProps {
  node: IterationGraphNode | null;
  onClose: () => void;
  onUseAsReference?: (node: IterationGraphNode) => void;
}

export function IterationNodeDetail({
  node,
  onClose,
  onUseAsReference,
}: IterationNodeDetailProps) {
  if (!node) return null;

  const formatDate = (timestamp: number) => {
    return formatInBeijing(timestamp, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const handleCopyPrompt = () => {
    navigator.clipboard.writeText(node.prompt);
  };

  const getStatusBadge = () => {
    if (node.isDeleted) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">
          <span className="w-2 h-2 rounded-full bg-slate-400" />
          已删除
        </span>
      );
    }
    switch (node.status) {
      case 'processing':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-50 text-amber-600 text-xs rounded-full">
            <Loader2 className="w-3 h-3 animate-spin" />
            生成中
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-50 text-red-600 text-xs rounded-full">
            <AlertCircle className="w-3 h-3" />
            失败
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-600 text-xs rounded-full">
            <Sparkles className="w-3 h-3" />
            完成
          </span>
        );
    }
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-4xl max-h-[85vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* 头部 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-light-border flex-shrink-0">
          <div className="flex items-center gap-3">
            <span className="px-2.5 py-1 bg-accent-blue text-white text-sm font-medium rounded-full">
              V{node.generation}
            </span>
            <h3 className="text-base font-medium text-light-text">迭代详情</h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-light-muted hover:text-light-text hover:bg-light-hover rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 内容 */}
        <div className="flex-1 overflow-y-auto">
          <div className="flex flex-col lg:flex-row">
            {/* 左侧图片 */}
            <div className="lg:w-1/2 p-6 bg-light-bg/30">
              <div className={`aspect-square rounded-xl overflow-hidden bg-light-hover border border-light-border ${node.isDeleted ? 'grayscale' : ''}`}>
                {node.isDeleted ? (
                  <div className="w-full h-full flex flex-col items-center justify-center bg-slate-100">
                    <div className="w-16 h-16 rounded-full bg-slate-300 flex items-center justify-center mb-3">
                      <span className="text-white text-xs font-bold">已删</span>
                    </div>
                    <p className="text-sm text-slate-500">该图片已被删除</p>
                  </div>
                ) : node.imageUrl ? (
                  <img
                    src={resolveBrowserMediaUrl(node.imageUrl)}
                    alt={node.prompt}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    {node.status === 'processing' ? (
                      <Loader2 className="w-12 h-12 animate-spin text-light-muted" />
                    ) : node.status === 'failed' ? (
                      <div className="text-center">
                        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-2" />
                        <p className="text-sm text-red-500">生成失败</p>
                        {node.errorMessage && (
                          <p className="text-xs text-light-muted mt-1">{node.errorMessage}</p>
                        )}
                      </div>
                    ) : (
                      <Sparkles className="w-12 h-12 text-light-muted" />
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* 右侧信息 */}
            <div className="lg:w-1/2 p-6 space-y-5">
              {/* 状态 */}
              <div className="flex items-center gap-2">
                {getStatusBadge()}
                <span className="text-xs text-light-muted">ID: {node.id.slice(-8)}</span>
              </div>

              {/* 提示词 */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-light-text">提示词</label>
                  <button
                    onClick={handleCopyPrompt}
                    className="flex items-center gap-1 text-xs text-accent-blue hover:text-accent-blue/80 transition-colors"
                  >
                    <Copy className="w-3 h-3" />
                    复制
                  </button>
                </div>
                <div className="p-4 bg-light-bg rounded-xl border border-light-border">
                  <p className="text-sm text-light-text leading-relaxed">{node.prompt}</p>
                </div>
              </div>

              {/* 参数信息 */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-light-bg rounded-xl border border-light-border">
                  <label className="text-[10px] text-light-muted uppercase tracking-wider">模型</label>
                  <p className="text-sm text-light-text font-medium">{node.model}</p>
                </div>
                <div className="p-3 bg-light-bg rounded-xl border border-light-border">
                  <label className="text-[10px] text-light-muted uppercase tracking-wider">尺寸</label>
                  <p className="text-sm text-light-text font-medium">{node.size}</p>
                </div>
                <div className="p-3 bg-light-bg rounded-xl border border-light-border">
                  <label className="text-[10px] text-light-muted uppercase tracking-wider">画质</label>
                  <p className="text-sm text-light-text font-medium">{node.quality}</p>
                </div>
                <div className="p-3 bg-light-bg rounded-xl border border-light-border">
                  <label className="text-[10px] text-light-muted uppercase tracking-wider">模式</label>
                  <p className="text-sm text-light-text font-medium">
                    {node.mode === 'text2img' ? '文生图' : node.mode === 'img2img' ? '图生图' : '局部重绘'}
                  </p>
                </div>
              </div>

              {/* 时间 */}
              <div className="flex items-center gap-2 text-xs text-light-muted">
                <Clock className="w-3.5 h-3.5" />
                <span>创建于 {formatDate(node.createdAt)}</span>
              </div>

              {/* 错误信息 */}
              {node.errorMessage && (
                <div className="p-3 bg-red-50 border border-red-100 rounded-xl">
                  <p className="text-xs text-red-600">
                    <span className="font-medium">错误: </span>
                    {node.errorMessage}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 底部操作 */}
        <div className="flex items-center gap-3 px-6 py-4 border-t border-light-border bg-light-bg/50 flex-shrink-0">
          {onUseAsReference && node.status === 'completed' && !node.isDeleted && (
            <button
              onClick={() => onUseAsReference(node)}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-accent-blue text-white text-sm font-medium rounded-xl hover:bg-accent-blue/90 transition-colors"
            >
              <Wand2 className="w-4 h-4" />
              作为参考图继续迭代
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default IterationNodeDetail;
